import React, { useRef, useMemo, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Box as MuiBox, useTheme } from '@mui/material';

import { Icon, JamIcon } from 'components/ui';
import { TableContext } from '../../../index';
import { ChipWithIcons } from '../../../components';
import { useActionMenuHandler } from '../../../hooks';
import { PatientActionsMenu } from './Menu';

import useDeactivatePatient from 'pages/PatientsPage/hooks/useDeactivatePatient';
import useReactivatePatient from 'pages/PatientsPage/hooks/useReactivatePatient';
import useDialog from 'lib/hooks/useDialog';
import DeactivateDialog from 'components/ui/DeactivateDialog';
import ReactivateDialog from 'components/ui/ReactivateDialog';
import getPermission from 'lib/utils/getPermission';
import useReinvitePatient from 'pages/PatientsPage/hooks/useReinvitePatient';
import ReinviteDialog from 'components/ui/ReinviteDialog';

const PatientActions = ({ patient, isActive }) => {
  const anchorRef = useRef(null);
  const history = useHistory();
  const theme = useTheme()

  const [
    open,
    onOpenMenu,
    handleListKeyDown,
    onCloseMenu,
  ] = useActionMenuHandler(anchorRef);

  const openConfirmDeactivateDialog = useDialog();
  const openConfirmReactivateDialog = useDialog();
  const openConfirmReinviteDialog = useDialog();
  const { deactivatePatient } = useDeactivatePatient();
  const { reactivatePatient } = useReactivatePatient();
  const { reinvitePatient } = useReinvitePatient();

  const handleDeactivatePatient = (patient) => {
    openConfirmDeactivateDialog
      .open({
        dialogTitle: (
          <MuiBox
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <JamIcon
              icon="stop-sign"
              style={{
                marginRight: '10px',
              }}
              color={theme.palette.colors.red100}
            />
            {t('Deactivate')}
          </MuiBox>
        ),
        dialogWarningMessage: t(
          'Are you sure you want to deactivate this user? Deactivation means that the user will no longer be able to log in or participate in any activities.'
        ),
        user: patient,
        isPatient: true,
      })
      .then(({ isDeleted }) => isDeleted && deactivatePatient(patient.id));
  };

  const handleReactivatePatient = (patient) => {
    openConfirmReactivateDialog
      .open({
        dialogTitle: (
          <MuiBox
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon
              icon="check-circle"
              style={{ marginRight: 6, color: theme.palette.colors.blue100 }}
            />
            {t('Reactivate')}
          </MuiBox>
        ),
        dialogWarningMessage: t(
          'To reactivate this patient, you need to select the care team to which the patient will be assigned.'
        ),
        user: patient,
      })
      .then(({ isReactivated, careTeam }) => {
        return (
          isReactivated &&
          reactivatePatient({
            id: patient.id,
            careTeamIds: [careTeam.id],
          })
        );
      });
  };

  const handleReinvitePatient = (patient) => {
    openConfirmReinviteDialog
      .open({
        dialogTitle: (
          <MuiBox
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon
              icon="paper-plane"
              style={{ marginRight: 6, color: theme.palette.colors.blue100 }}
            />
            {t('Reinvite')}
          </MuiBox>
        ),
        dialogWarningMessage: t(
          'This will send a new invite to the patient with one-time use credentials, rendering any previous invites invalid.'
        ),
        user: patient,
      })
      .then(({ isReinvited }) => {
        return (
          isReinvited &&
          reinvitePatient({
            id: patient.id,
          })
        );
      });
  };

  const { onAddCarePlan, onOpenAppointmentDialog } = useContext(TableContext);
  const { t } = useTranslation();

  const options = useMemo(
    () => [
      // {
      //   label: t('Send message'),
      //   action: () => history.push('/chat'),
      //   icon: 'comment-lines',
      // },
      {
        label: t('Schedule appointment'),
        action: () => onOpenAppointmentDialog(patient),
        icon: 'calendar-plus',
      },
      // {
      //   label: t('Share record'),
      //   action: () => {},
      //   icon: 'share',
      // },
      {
        label: t('Add protocol'),
        action: () => onAddCarePlan(patient),
        icon: 'hands-heart',
        variant: 'contained',
      },
      ...(!patient.isSelfRegistered && patient.lastActivity === null
        ? [
            {
              label: t('Reinvite'),
              action: () => handleReinvitePatient(patient),
              icon: 'paper-plane',
            },
          ]
        : []),
      ...(getPermission('PATIENTS_MANAGEMENT', 'delete') && isActive
        ? [
            {
              label: t('Deactivate'),
              action: () => handleDeactivatePatient(patient),
              icon: 'ban',
            },
          ]
        : []),
      ...(getPermission('DEACTIVATED_PATIENTS_MANAGEMENT', 'update') &&
      !isActive
        ? [
            {
              label: t('Reactivate'),
              action: () => handleReactivatePatient(patient),
              icon: 'check-circle',
            },
          ]
        : []),
    ],
    [patient, history, onAddCarePlan, onOpenAppointmentDialog, t, isActive]
  );

  return (
    <>
      <MuiBox ref={anchorRef}>
        <ChipWithIcons
          data-id="actions-button"
          label={<Icon icon="ellipsis-v" />}
          background={theme.palette.colors.white}
          onClick={onOpenMenu}
        />
      </MuiBox>
      <PatientActionsMenu
        open={open}
        anchorRef={anchorRef}
        onCloseMenu={onCloseMenu}
        handleListKeyDown={handleListKeyDown}
        options={options}
        isActive={isActive}
      />
      {openConfirmDeactivateDialog.isOpen && (
        <DeactivateDialog
          isOpen={openConfirmDeactivateDialog}
          initialData={openConfirmDeactivateDialog.initialData}
          close={openConfirmDeactivateDialog.close}
        />
      )}
      {openConfirmReactivateDialog.isOpen && (
        <ReactivateDialog
          isOpen={openConfirmReactivateDialog}
          initialData={openConfirmReactivateDialog.initialData}
          close={openConfirmReactivateDialog.close}
        />
      )}
      {openConfirmReinviteDialog.isOpen && (
        <ReinviteDialog
          isOpen={openConfirmReinviteDialog}
          initialData={openConfirmReinviteDialog.initialData}
          close={openConfirmReinviteDialog.close}
        />
      )}
    </>
  );
};

export default PatientActions;
